import { Extension } from "@tiptap/core";
import { Plugin } from "@tiptap/pm/state";
import { runInAction } from "mobx";

import { nodeIsExpression } from "@/components/tiptap/tiptap-expression/utils";

import type { DocumentModel } from "@/models/DocumentModel";
import type { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";

type WhitespaceClickExtensionOptions = {
  doc: DocumentModel;
};

/**
 * This extension detects the mouse click on the empty space to the right from the content. If the
 * last element of this line is expression, it will put cursor to the last position of this expression.
 */
export const WhitespaceClick = Extension.create<WhitespaceClickExtensionOptions>({
  name: "whitespaceClick",

  addProseMirrorPlugins() {
    const { doc } = this.options;
    return [
      new Plugin({
        props: {
          handleDOMEvents: {
            mousedown: (view, event) => {
              if (event.button === 2) {
                return false;
              }

              if (event.shiftKey) {
                if (doc.cursor.isActive || doc.selection.startSelectionPosition) {
                  const clickPos = view.posAtCoords({
                    left: event.clientX,
                    top: event.clientY,
                  })?.pos;

                  if (clickPos !== undefined) {
                    const applied = doc.selection.extendSelectionFromMathCursor(clickPos);

                    if (applied) {
                      event.preventDefault();
                      return true;
                    }
                  }
                }
                return false;
              }

              runInAction(function resetCursorContext() {
                doc.cursorContextId = undefined;
                doc.cursorContext().selection.resetSelection();
              });

              const coords = { left: event.clientX, top: event.clientY };
              const pos = view.posAtCoords(coords);

              if (!pos) return false;

              const isOutsideNodes =
                pos.inside === -1 && view.state.doc.resolve(pos.pos).depth === 0;
              const resolvePos = isOutsideNodes ? Math.max(0, pos.pos - 1) : pos.pos;

              const $pos = view.state.doc.resolve(resolvePos);

              if ($pos.depth === 0) return false;

              const start = $pos.before($pos.depth);
              const end = $pos.end($pos.depth);

              let lineEnd = end;

              view.state.doc.nodesBetween(start, end, (node, nodePos) => {
                if (node.type.name !== "hardBreak") {
                  return;
                }

                if (nodePos >= resolvePos && lineEnd === end) {
                  lineEnd = nodePos;
                }
              });

              const box = view.coordsAtPos(lineEnd);

              if (event.clientX > box.right) {
                const lastNodeInLine = view.state.doc.resolve(lineEnd).nodeBefore;

                if (!lastNodeInLine || !nodeIsExpression(lastNodeInLine)) {
                  return false;
                }

                const wrapperNode = doc.nodes.get(lastNodeInLine.attrs.nodeId ?? "") as
                  | WrapperNodeModel
                  | undefined;

                if (wrapperNode) {
                  event.preventDefault();
                  doc.editor.resetInput();
                  doc.editor.hints.setPhantomHints([]);
                  doc.cursor.moveTo(wrapperNode.inlineNode, wrapperNode.inlineNode?.nodes.length);
                  doc.selection.startPointerSelection();

                  return true;
                }
              }
            },
          },
        },
      }),
    ];
  },
});
